"use client";

import { motion, AnimatePresence } from "framer-motion";
import React, { useState, RefObject, useEffect } from "react";
import RetirementChoosePlanScreen from "../process-parts/RetirementChoosePlanScreen";
import RetirementAgeScreen from "../process-parts/RetirementAgeScreen";
import RetirementIncomeScreen from "../process-parts/RetirementIncomeScreen";
import SummaryPreRetirement from "../steps/SummaryPreRetirement";
import { PortfolioApiQuestion } from "@/type/servicesPageTypes";
import { Plan, PlanningMode, RetirementFlowState, RetirementSummaryValues } from "@/type/financialPageTypes";

type Screen = "choose-plan" | "age" | "income" | "summary";
type Answer = {
  question_id: number;
  option_id: number;
  answer: string;
};

const slideVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 24 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -24 }),
};

export default function PreRetirementFlow({
  currentStep,
  scrollContainerRef,
  questions,
  onAnswersChangeAction,
  onSummaryChangeAction,
  validateTrigger,
}: {
  currentStep: number;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  questions: PortfolioApiQuestion[];
  onAnswersChangeAction?: (answers: Record<number, Answer>) => void;
  onSummaryChangeAction?: (values: RetirementSummaryValues) => void;
  validateTrigger?: number;
}) {
  const SCREENS: Screen[] = ["choose-plan", "age", "income", "summary"];
  const screen: Screen = SCREENS[currentStep - 1] ?? "choose-plan";

  const [answers, setAnswers] = useState<Record<number, Answer>>({});

  const questionMap = Object.fromEntries(questions.map((q) => [q.id, q]));

  const choosePlanQuestion = questionMap[110];
  const planningQuestion = questionMap[111];
  const currentAgeQuestion = questionMap[112];
  const retirementAgeQuestion = questionMap[145];
  const monthlyIncomeQuestion = questionMap[146];
  const salaryPercentQuestion = questionMap[147];
  const monthlyExpensesQuestion = questionMap[148];
  const targetCorpusQuestion = questionMap[149];

  const currentAgeQuestionId = currentAgeQuestion?.id;
  const retirementAgeQuestionId = retirementAgeQuestion?.id;
  const salaryPercentQuestionId = salaryPercentQuestion?.id;
  const monthlyIncomeQuestionId = monthlyIncomeQuestion?.id;
  const monthlyExpensesQuestionId = monthlyExpensesQuestion?.id;
  const targetCorpusQuestionId = targetCorpusQuestion?.id;

  useEffect(() => {
    onAnswersChangeAction?.(answers);
  }, [answers, onAnswersChangeAction]);

  const updateAnswer = (questionId: number, optionId: number, answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { question_id: questionId, option_id: optionId, answer },
    }));
  };

  const clearAnswer = (questionId: number) => {
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const [state, setState] = useState<RetirementFlowState>({
    selectedPlan: null,
    planningMode: null,
    currentAge: 28,
    retirementAge: 65,
    monthlyIncome: "50000",
    salaryPercent: 25,
    customAmount: "10000000",
  });

  // Keep the age/salary answers in sync with their defaults even if the user
  // never touches the sliders, so they're still included in the submit payload.
  useEffect(() => {
    if (currentAgeQuestionId) updateAnswer(currentAgeQuestionId, 0, state.currentAge.toString());
  }, [state.currentAge, currentAgeQuestionId]);

  useEffect(() => {
    if (retirementAgeQuestionId) updateAnswer(retirementAgeQuestionId, 0, state.retirementAge.toString());
  }, [state.retirementAge, retirementAgeQuestionId]);

  // Salary % (147) only applies to salary mode.
  useEffect(() => {
    if (!salaryPercentQuestionId) return;
    if (state.planningMode === "salary") {
      updateAnswer(salaryPercentQuestionId, 0, state.salaryPercent.toString());
    } else {
      clearAnswer(salaryPercentQuestionId);
    }
  }, [state.salaryPercent, state.planningMode, salaryPercentQuestionId]);

  // "Monthly Income" (146) and "Monthly Expenses" (148) share the same input;
  // only the question matching the active planning mode is sent, the other is cleared.
  useEffect(() => {
    const isSalaryMode = state.planningMode === "salary";
    const isExpensesMode = state.planningMode === "expenses";

    if (isSalaryMode) {
      if (monthlyExpensesQuestionId) clearAnswer(monthlyExpensesQuestionId);
      if (monthlyIncomeQuestionId) updateAnswer(monthlyIncomeQuestionId, 0, state.monthlyIncome);
    } else if (isExpensesMode) {
      if (monthlyIncomeQuestionId) clearAnswer(monthlyIncomeQuestionId);
      if (monthlyExpensesQuestionId) updateAnswer(monthlyExpensesQuestionId, 0, state.monthlyIncome);
    } else {
      if (monthlyIncomeQuestionId) clearAnswer(monthlyIncomeQuestionId);
      if (monthlyExpensesQuestionId) clearAnswer(monthlyExpensesQuestionId);
    }
  }, [state.monthlyIncome, state.planningMode, monthlyIncomeQuestionId, monthlyExpensesQuestionId]);

  // Target corpus (149) only applies to custom mode.
  useEffect(() => {
    if (!targetCorpusQuestionId) return;
    if (state.planningMode === "custom") {
      updateAnswer(targetCorpusQuestionId, 0, state.customAmount);
    } else {
      clearAnswer(targetCorpusQuestionId);
    }
  }, [state.customAmount, state.planningMode, targetCorpusQuestionId]);

  const sipTenure = Math.max(0, state.retirementAge - state.currentAge);

  return (
    <AnimatePresence mode="wait" custom={currentStep}>
      <motion.div
        key={screen}
        custom={currentStep}
        ref={scrollContainerRef}
        variants={slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.22, ease: "easeInOut" }}
        className="relative h-full p-4 sm:p-6"
      >
        {screen === "choose-plan" && (
          <RetirementChoosePlanScreen
            stepTitle={choosePlanQuestion?.question}
            planningStepTitle={planningQuestion?.question}
            selectedPlan={state.selectedPlan}
            onSelectPlan={(plan: Plan) => {
              setState((s) => ({ ...s, selectedPlan: plan, planningMode: null }));
              clearAnswer(planningQuestion?.id ?? 111);
              if (plan) {
                const option = choosePlanQuestion?.options.find((o) => o.option === plan);
                if (option) updateAnswer(choosePlanQuestion.id, option.id, option.option);
              } else {
                clearAnswer(choosePlanQuestion?.id ?? 110);
              }
            }}
            planningMode={state.planningMode}
            onSelectMode={(mode: PlanningMode) => {
              setState((s) => ({ ...s, planningMode: mode, selectedPlan: null }));
              clearAnswer(choosePlanQuestion?.id ?? 110);
              if (mode) {
                const option = planningQuestion?.options.find((o) => {
                  switch (mode) {
                    case "salary":
                      return o.option === "Based on my salary";
                    case "expenses":
                      return o.option === "Based on desired future expenses";
                    case "custom":
                      return o.option === "Want to enter Custom Retirement amount";
                    default:
                      return false;
                  }
                });
                if (option) updateAnswer(planningQuestion.id, option.id, option.option);
              } else {
                clearAnswer(planningQuestion?.id ?? 111);
              }
            }}
            customAmount={state.customAmount}
            onCustomAmount={(v) => setState((s) => ({ ...s, customAmount: v }))}
            validateTrigger={validateTrigger}
          />
        )}

        {screen === "age" && (
          <RetirementAgeScreen
            stepTitle={retirementAgeQuestion?.stepper_title}
            currentAgeLabel={currentAgeQuestion?.question}
            retirementAgeLabel={retirementAgeQuestion?.question}
            currentAge={state.currentAge}
            retirementAge={state.retirementAge}
            onCurrentAge={(age) => {
              setState((s) => ({ ...s, currentAge: age }));
            }}
            onRetirementAge={(age) => {
              setState((s) => ({ ...s, retirementAge: age }));
            }}
            validateTrigger={validateTrigger}
          />
        )}

        {screen === "income" && (
          <RetirementIncomeScreen
            stepTitle={monthlyIncomeQuestion?.question}
            expensesStepTitle={monthlyExpensesQuestion?.question}
            customStepTitle={targetCorpusQuestion?.question}
            salaryPercentLabel={salaryPercentQuestion?.question}
            monthlyIncome={state.monthlyIncome}
            onMonthlyIncomeAction={(value) => {
              setState((s) => ({ ...s, monthlyIncome: value }));
            }}
            salaryPercent={state.salaryPercent}
            onSalaryPercentAction={(percent) => {
              setState((s) => ({ ...s, salaryPercent: percent }));
            }}
            customAmount={state.customAmount}
            onCustomAmountAction={(value) => {
              setState((s) => ({ ...s, customAmount: value }));
            }}
            planningMode={state.planningMode}
            sipTenure={sipTenure}
            validateTrigger={validateTrigger}
          />
        )}

        {screen === "summary" && (
          <SummaryPreRetirement
            selectedPlan={state.selectedPlan}
            planningMode={state.planningMode}
            currentAge={state.currentAge}
            retirementAge={state.retirementAge}
            monthlyIncome={state.monthlyIncome}
            salaryPercent={state.salaryPercent}
            customAmount={state.customAmount}
            onSummaryChangeAction={onSummaryChangeAction}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}
